import * as React from 'react';
import { Check } from 'lucide-react';

import { cn } from '../../utils/cn';
import { QUESTIONNAIRE_COLOUR } from './questionnaire-tokens';

export interface RecommendedCardProps {
    title: string;
    description?: string;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    className?: string;
}

/**
 * The pre-selected suggestion that opens a step: raised off the page in white
 * so it reads as the answer already given rather than the first of a list.
 *
 * The reader can clear it — the suggestion is a starting point, not a fixed
 * answer, and a checkbox that will not turn off would say otherwise.
 */
export const RecommendedCard = ({
    title,
    description,
    checked,
    onCheckedChange,
    className,
}: RecommendedCardProps) => {
    const descriptionId = `recommended-card-${title
        .toLowerCase()
        .replace(/\s+/g, '-')}`;

    return (
        <button
            aria-checked={checked}
            // Named by the title alone, described by the rest. Left to itself the
            // accessible name would be the whole card, so the choice would be read
            // out as a paragraph every time focus landed on it.
            aria-describedby={description ? descriptionId : undefined}
            aria-label={title}
            className={cn(
                'flex w-full flex-col gap-3.5 rounded-3 bg-white p-4.25 text-left shadow-[0_4px_16px_rgba(0,0,0,0.08)] transition-shadow',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#181818]',
                'hover:shadow-[0_6px_20px_rgba(0,0,0,0.11)]',
                className,
            )}
            onClick={() => onCheckedChange(!checked)}
            role="checkbox"
            type="button"
        >
            <span className="flex w-full items-start justify-between gap-4">
                <span
                    className={cn(
                        'font-sans text-5.25 leading-[1.2] font-medium tracking-[-0.02em]',
                        QUESTIONNAIRE_COLOUR.ink,
                    )}
                >
                    {title}
                </span>
                <span
                    className={cn(
                        'flex size-7 shrink-0 items-center justify-center rounded-1.5 border transition-colors',
                        checked
                            ? 'border-[#181818] bg-[#181818] text-white'
                            : 'border-[#c7c7c7] bg-white',
                    )}
                >
                    {checked ? (
                        <Check aria-hidden="true" className="size-4.5" />
                    ) : null}
                </span>
            </span>
            {description ? (
                <span
                    className={cn(
                        'font-sans text-4 leading-[1.3] tracking-[-0.01em]',
                        QUESTIONNAIRE_COLOUR.inkSubtle,
                    )}
                    id={descriptionId}
                >
                    {description}
                </span>
            ) : null}
        </button>
    );
};
